import { sdk, API_FUNCTIONS, queryClient, queryKeys, checkIfGreatSuccess, RULES_ENDPOINTS } from 'utils/query';
import { RuleItem, RuleTransition, RuleTransitions } from 'models/rules';
import { useTranslation } from 'react-i18next';
import { ApiTaskResult } from 'models/general';
import { QueryKey } from '@tanstack/react-query';
import { useDrawerContext } from 'context/hooks';
import { useCustomMutation } from '../useCustomMutation';

interface ChangeStatusRuleProps {
  item: RuleItem;
  transition: RuleTransition;
}

const changeStatusRule = async ({ item, transition }: ChangeStatusRuleProps) => {
  const result = await sdk.tasks.api.post<ApiTaskResult, unknown>(
    API_FUNCTIONS.monitoring,
    `${RULES_ENDPOINTS.monitoringRules}/${item.id}/${transition}`,
    {},
    {},
  );
  return checkIfGreatSuccess({ result });
};

interface ChangeRuleProps {
  reset?: () => void;
}

export const useChangeStatusRule = ({ reset }: ChangeRuleProps) => {
  const { t } = useTranslation();
  const { closeDrawer } = useDrawerContext();

  return useCustomMutation({
    fn: changeStatusRule,
    onSuccess: {
      message: t('success_data_updated', { value: t('alerts_rule_label') }),
      callback: async (_result, variables) => {
        const {
          item: { patientId, groupId, id },
        } = variables;
        if (variables.transition === RuleTransitions.enable) {
          closeDrawer();
        }

        const queryKey = patientId
          ? [`${queryKeys.patientRules}-${patientId}-${groupId}`]
          : [`${queryKeys.groupRules}-${groupId}`];

        const keys: QueryKey[] = [[queryKeys.ruleData, id], queryKey];

        await Promise.all(
          keys.map(async (key) => {
            await queryClient.invalidateQueries({ queryKey: key });
          }),
        );
      },
    },
    onError: {
      reset,
    },
  });
};
